import {memo, RefObject, useMemo} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";

import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {Country} from "@pg-rp/common-types";

import {IRPStore} from "../../../app/rp_reducer";
import {GTMEventApplicationSourceSection} from "../../../application/types/lead/GTMEvenApplicationSourceSection";
import {TimeStamp} from "../../../atoms/TimeStamp";
import {TwoColumnGridHolder, TwoColumnGridMain} from "../../../atoms/TwoColumnGrid";
import {OfferRankingModule} from "../../../ranking/components/OfferRankingModule";
import {getRankingItem} from "../../../ranking/utils/get_ranking_item";
import {TagType} from "../../../types/TagType";
import {IValueLabel} from "../../../variables/types/IValueLabel";
import {OfferBarNavigation} from "../../atoms/OfferBarNavigation";
import {OfferStickyBar} from "../../atoms/OfferStickyBar";
import {getOfferAmenities} from "../../helpers/get_offer_amenities";
import {IOfferDetail} from "../../types/IOfferDetail";
import {IOfferTag} from "../../types/IOfferTag";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {OfferDetailAmenities} from "./OfferDetailAmenities";
import {OfferDetailDescription} from "./OfferDetailDescription";
import {OfferDetailDetails} from "./OfferDetailDetails";
import {OfferDetailLocation} from "./OfferDetailLocation";
import {OfferVideo} from "./OfferVideo";
import {OfferViewSectionTracker} from "./OfferViewSectionTracking";

interface IProps {
    offer: IOfferDetail;
    refElems: {
        refGallery: RefObject<HTMLTableSectionElement | null>;
        refDescription: RefObject<HTMLTableSectionElement | null>;
        refLocation: RefObject<HTMLTableSectionElement | null>;
    };
    changeModalTab: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    variables: {
        offer_natural_sites: IValueLabel[];
        offer_standard_description_type: IValueLabel[];
        offer_facilities: IValueLabel[];
        offer_funds_protection: IValueLabel[];
    } | null;
    hasDescription: boolean;
    shouldMapBeRendered?: boolean;
}

export const OfferDetailLimited = memo((props: IProps) => {
    const {offer, refElems, changeModalTab, variables, hasDescription, shouldMapBeRendered} = props;

    const publishDate = !offer.configuration.hide_publish_date && offer.configuration.publish_date ? offer.configuration.publish_date : null;
    const getNearbyParksTag = offer.tags.filter((tag: IOfferTag) => tag.id === TagType.GREEN_AREAS);

    const groupedOfferAmenities = useMemo(
        () => getOfferAmenities(offer.facilities, offer.natural_sites, getNearbyParksTag, offer.standard_description.types, variables),
        [offer.facilities, offer.natural_sites, offer.standard_description.types, variables]
    );

    const rankingItem = getRankingItem(offer.id);
    const transportPoiData = useSelector((state: IRPStore) => state.offerDetail.transportPoi);

    return (
        <>
            <OfferStickyBar className={offerStickyBarWrapperStyle} offer={offer} onButtonClick={changeModalTab} />

            <OfferBarNavigation offer={offer} refElements={refElems} disableContact />

            <TwoColumnGridHolder>
                <TwoColumnGridMain>
                    {groupedOfferAmenities.length > 0 && (
                        <section className={offerDetailAmenitiesSection}>
                            <OfferViewSectionTracker itemId={offer.id} sectionName="amenities" />
                            <OfferDetailAmenities offerAmenities={groupedOfferAmenities} />
                        </section>
                    )}

                    <section className={detailsSectionStyle}>
                        <OfferViewSectionTracker itemId={offer.id} sectionName="investment_details" />
                        <OfferDetailDetails offer={offer} variables={variables} showAmenities={groupedOfferAmenities.length === 0} />
                    </section>

                    {rankingItem && (
                        <section id="ranking" className={detailsSectionStyle}>
                            <OfferViewSectionTracker itemId={offer.id} sectionName="ranking" />
                            <OfferRankingModule rankingItem={rankingItem} />
                        </section>
                    )}

                    {offer.video && (
                        <section className={detailsSectionStyle}>
                            <OfferViewSectionTracker itemId={offer.id} sectionName="video" />
                            <OfferVideo url={offer.video} />
                        </section>
                    )}

                    {hasDescription && (
                        <section
                            id="opis"
                            ref={refElems.refDescription}
                            className={scrollOffset}
                            data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_DESCRIPTION_SECTION}
                        >
                            <OfferViewSectionTracker itemId={offer.id} sectionName="description" />
                            <OfferDetailDescription offer={offer} />
                        </section>
                    )}

                    {offer.region.country === Country.POLAND && shouldMapBeRendered && (
                        <section
                            id="okolica"
                            ref={refElems.refLocation}
                            className={scrollOffset}
                            data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_LOCATION_SECTION}
                        >
                            {transportPoiData && transportPoiData.poi && (
                                <>
                                    <OfferViewSectionTracker itemId={offer.id} sectionName="location" />
                                    <OfferDetailLocation
                                        offer={offer}
                                        geoAreaCoordinates={offer.geo_area.coordinates}
                                        geoPointCoordinates={offer.geo_point.coordinates}
                                        changeModalTab={changeModalTab}
                                        pois={transportPoiData.poi.pois}
                                    />
                                </>
                            )}
                        </section>
                    )}

                    <section id="timestamp" className={timeStampSectionStyle}>
                        <TimeStamp lastUpdateDate={offer.stats?.last_update ?? ""} publishDate={publishDate} />
                    </section>
                </TwoColumnGridMain>
            </TwoColumnGridHolder>
        </>
    );
});

export const offerDescriptionSectionHeader = css({
    p: "3rem 0",
    fontSize: "1.8rem",

    sm: {
        fontSize: "2.2rem",
        p: "3rem 0"
    }
});

const offerStickyBarWrapperStyle = css({
    m: "0 0 0.3rem 0",
    display: "none",

    md: {
        display: "block",
        m: "0 0 2.4rem 0"
    }
});

const timeStampSectionStyle = css({
    mb: 1,

    md: {
        mb: 3
    }
});

const offerDetailAmenitiesSection = css({
    m: "0 0 1.6rem 0",

    md: {
        m: "0 0 2.4rem 0"
    }
});

const detailsSectionStyle = css({
    m: "0 0 1.6rem 0",

    md: {
        m: "0 0 2.4rem 0"
    }
});

const scrollOffset = css({
    pt: "8rem",
    mt: "-8rem"
});
import {memo, RefObject, useMemo} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";

import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {Country} from "@pg-rp/common-types";

import {IRPStore} from "../../../app/rp_reducer";
import {GTMEventApplicationSourceSection} from "../../../application/types/lead/GTMEvenApplicationSourceSection";
import {TimeStamp} from "../../../atoms/TimeStamp";
import {TwoColumnGridHolder, TwoColumnGridMain} from "../../../atoms/TwoColumnGrid";
import {OfferRankingModule} from "../../../ranking/components/OfferRankingModule";
import {getRankingItem} from "../../../ranking/utils/get_ranking_item";
import {TagType} from "../../../types/TagType";
import {IValueLabel} from "../../../variables/types/IValueLabel";
import {OfferBarNavigation} from "../../atoms/OfferBarNavigation";
import {OfferStickyBar} from "../../atoms/OfferStickyBar";
import {getOfferAmenities} from "../../helpers/get_offer_amenities";
import {IOfferDetail} from "../../types/IOfferDetail";
import {IOfferTag} from "../../types/IOfferTag";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {OfferDetailAmenities} from "./OfferDetailAmenities";
import {OfferDetailDescription} from "./OfferDetailDescription";
import {OfferDetailDetails} from "./OfferDetailDetails";
import {OfferDetailLocation} from "./OfferDetailLocation";
import {OfferVideo} from "./OfferVideo";
import {OfferViewSectionTracker} from "./OfferViewSectionTracking";

interface IProps {
    offer: IOfferDetail;
    refElems: {
        refGallery: RefObject<HTMLTableSectionElement | null>;
        refDescription: RefObject<HTMLTableSectionElement | null>;
        refLocation: RefObject<HTMLTableSectionElement | null>;
    };
    changeModalTab: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    variables: {
        offer_natural_sites: IValueLabel[];
        offer_standard_description_type: IValueLabel[];
        offer_facilities: IValueLabel[];
        offer_funds_protection: IValueLabel[];
    } | null;
    hasDescription: boolean;
    shouldMapBeRendered?: boolean;
}

export const OfferDetailLimited = memo((props: IProps) => {
    const {offer, refElems, changeModalTab, variables, hasDescription, shouldMapBeRendered} = props;

    const publishDate = !offer.configuration.hide_publish_date && offer.configuration.publish_date ? offer.configuration.publish_date : null;
    const getNearbyParksTag = offer.tags.filter((tag: IOfferTag) => tag.id === TagType.GREEN_AREAS);

    const groupedOfferAmenities = useMemo(
        () => getOfferAmenities(offer.facilities, offer.natural_sites, getNearbyParksTag, offer.standard_description.types, variables),
        [offer.facilities, offer.natural_sites, offer.standard_description.types, variables]
    );

    const rankingItem = getRankingItem(offer.id);
    const transportPoiData = useSelector((state: IRPStore) => state.offerDetail.transportPoi);

    return (
        <>
            <OfferStickyBar className={offerStickyBarWrapperStyle} offer={offer} onButtonClick={changeModalTab} />

            <OfferBarNavigation offer={offer} refElements={refElems} disableContact />

            <TwoColumnGridHolder>
                <TwoColumnGridMain>
                    {groupedOfferAmenities.length > 0 && (
                        <section className={offerDetailAmenitiesSection}>
                            <OfferViewSectionTracker itemId={offer.id} sectionName="amenities" />
                            <OfferDetailAmenities offerAmenities={groupedOfferAmenities} />
                        </section>
                    )}

                    <section className={detailsSectionStyle}>
                        <OfferViewSectionTracker itemId={offer.id} sectionName="investment_details" />
                        <OfferDetailDetails offer={offer} variables={variables} showAmenities={groupedOfferAmenities.length === 0} />
                    </section>

                    {rankingItem && (
                        <section id="ranking" className={detailsSectionStyle}>
                            <OfferViewSectionTracker itemId={offer.id} sectionName="ranking" />
                            <OfferRankingModule rankingItem={rankingItem} />
                        </section>
                    )}

                    {offer.video && (
                        <section className={detailsSectionStyle}>
                            <OfferViewSectionTracker itemId={offer.id} sectionName="video" />
                            <OfferVideo url={offer.video} />
                        </section>
                    )}

                    {hasDescription && (
                        <section
                            id="opis"
                            ref={refElems.refDescription}
                            className={scrollOffset}
                            data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_DESCRIPTION_SECTION}
                        >
                            <OfferViewSectionTracker itemId={offer.id} sectionName="description" />
                            <OfferDetailDescription offer={offer} />
                        </section>
                    )}

                    {offer.region.country === Country.POLAND && shouldMapBeRendered && (
                        <section
                            id="okolica"
                            ref={refElems.refLocation}
                            className={scrollOffset}
                            data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_LOCATION_SECTION}
                        >
                            {transportPoiData && transportPoiData.poi && (
                                <>
                                    <OfferViewSectionTracker itemId={offer.id} sectionName="location" />
                                    <OfferDetailLocation
                                        offer={offer}
                                        geoAreaCoordinates={offer.geo_area.coordinates}
                                        geoPointCoordinates={offer.geo_point.coordinates}
                                        changeModalTab={changeModalTab}
                                        pois={transportPoiData.poi.pois}
                                    />
                                </>
                            )}
                        </section>
                    )}

                    <section id="timestamp" className={timeStampSectionStyle}>
                        <TimeStamp lastUpdateDate={offer.stats?.last_update ?? ""} publishDate={publishDate} />
                    </section>
                </TwoColumnGridMain>
            </TwoColumnGridHolder>
        </>
    );
});

export const offerDescriptionSectionHeader = css({
    p: "3rem 0",
    fontSize: "1.8rem",

    sm: {
        fontSize: "2.2rem",
        p: "3rem 0"
    }
});

const offerStickyBarWrapperStyle = css({
    m: "0 0 0.3rem 0",
    display: "none",

    md: {
        display: "block",
        m: "0 0 2.4rem 0"
    }
});

const timeStampSectionStyle = css({
    mb: 1,

    md: {
        mb: 3
    }
});

const offerDetailAmenitiesSection = css({
    m: "0 0 1.6rem 0",

    md: {
        m: "0 0 2.4rem 0"
    }
});

const detailsSectionStyle = css({
    m: "0 0 1.6rem 0",

    md: {
        m: "0 0 2.4rem 0"
    }
});

const scrollOffset = css({
    pt: "8rem",
    mt: "-8rem"
});
